import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { initialPartnerState, PartnerState } from '../models/state/partner.state';
import { inject } from '@angular/core';
import { ApplicationFileService, ApplicationService, PartnerService } from '../services';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ApplicationFile, PageRequest, PartnerCreateUpdateRequest } from '../models';
import { of, pipe, throwError } from 'rxjs';
import { catchError, concatMap, finalize, map, switchMap, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

export const PartnerStore = signalStore(
  { providedIn: 'root' },
  withState<PartnerState>(initialPartnerState),
  withMethods(
    (
      store,
      applicationService = inject(ApplicationService),
      applicationFileService = inject(ApplicationFileService),
      partnerService = inject(PartnerService),
      toastrService = inject(ToastrService),
      translateService = inject(TranslateService),
    ) => ({
      loadPartners: rxMethod<PageRequest>(
        pipe(
          map((_pageRequest) => {
            const pageRequest = { ..._pageRequest, page: 0, size: applicationService.itemsPerPage() };
            patchState(store, { loading: true, pageRequest });
            return pageRequest;
          }),
          switchMap((pageRequest) =>
            partnerService.getPartnersByRole(pageRequest).pipe(
              tap((partners) => patchState(store, { partners })),
              catchError(() => of()), // Prevent rxMethod from crashing
              finalize(() => patchState(store, { loading: false })),
            ),
          ),
        ),
      ),
      loadAllPartners: rxMethod<void>(
        switchMap(() =>
          partnerService.getAllPartners().pipe(
            tap((partners) => patchState(store, { allPartners: partners })),
            catchError(() => of()), // Prevent rxMethod from crashing
          ),
        ),
      ),

      loadMorePartners: rxMethod<void>(
        pipe(
          map(() => {
            const { page, ...restOfPageRequest } = store.pageRequest();
            const pageRequest = { ...restOfPageRequest, page: (page ?? 0) + 1 };
            patchState(store, { pageRequest });
            return pageRequest;
          }),
          concatMap((pageRequest) =>
            partnerService
              .getPartners(pageRequest)
              .pipe(tap((partners) => patchState(store, { partners: [...store.partners(), ...partners] }))),
          ),
        ),
      ),
      createPartner(partnerData: PartnerCreateUpdateRequest) {
        patchState(store, { saving: true });

        return partnerService.createPartner(partnerData).pipe(
          tap((partner) => {
            patchState(store, {
              partners: [...store.partners(), partner],
            });

            toastrService.success(translateService.instant('partners.list.alerts.createSuccess'));
          }),
          catchError((error) => {
            toastrService.error(
              translateService.instant(
                error.status === 409
                  ? 'partners.list.alerts.emailAlreadyUsedError'
                  : error.status === 403
                    ? 'common.alerts.permissionDenied'
                    : 'common.alerts.genericError',
              ),
            );

            return throwError(() => error);
          }),
          finalize(() => patchState(store, { saving: false })),
        );
      },
      updatePartner(id: string, partnerData: PartnerCreateUpdateRequest) {
        patchState(store, { saving: true });

        return partnerService.updatePartner(id, partnerData).pipe(
          switchMap((partner) =>
            (partnerData.logoFile != null
              ? applicationFileService.uploadPartnerLogo(partner.id, partnerData.logoFile as File)
              : of({} as ApplicationFile)
            ).pipe(
              tap((logo) => {
                patchState(store, {
                  partners: store.partners().map((p) =>
                    p.id === id
                      ? {
                          ...p,
                          ...partner,
                          logo: logo.id ? logo : partner.logo,
                        }
                      : p,
                  ),
                });

                toastrService.success(translateService.instant('partners.list.alerts.updateSuccess'));
              }),
              catchError((error) => {
                toastrService.error(
                  translateService.instant(
                    error.status === 409
                      ? 'partners.list.alerts.emailAlreadyUsedError'
                      : error.status === 403
                        ? 'common.alerts.permissionDenied'
                        : 'common.alerts.genericError',
                  ),
                );

                return throwError(() => error);
              }),
            ),
          ),
          finalize(() => patchState(store, { saving: false })),
        );
      },
      deletePartner: rxMethod<string>(
        pipe(
          switchMap((id) =>
            partnerService.deletePartner(id).pipe(
              tap(() => {
                patchState(store, {
                  partners: [...store.partners().filter((partner) => partner.id !== id)],
                });
                toastrService.success(translateService.instant('partners.list.alerts.deleteSuccess'));
              }),
              catchError((error) => {
                toastrService.error(
                  translateService.instant(
                    error.status === 403 ? 'common.alerts.permissionDenied' : 'common.alerts.genericError',
                  ),
                );
                return of();
              }),
            ),
          ),
        ),
      ),
    }),
  ),
);
